import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import {
  Calendar,
  RefreshCw,
  Play,
  Clock,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Settings2,
  Link as LinkIcon,
} from 'lucide-react';
import { useMode } from '@/providers/ModeProvider';
import { useIntegration } from '@/providers/IntegrationProvider';
import { useToast } from '@/hooks/use-toast';
import { periodManagementService } from '@/services/period-management-service';
import type {
  PeriodSettings,
  RunPeriodEndResponse,
  SyncPeriodResponse,
} from '@/types/settings';
import { usePeriodManagementPermissions } from '@/hooks/use-permissions';

export default function PeriodManagementPage(): React.ReactElement {
  const { isDemo } = useMode();
  const { settings: integrationSettings, isLoading: isIntegrationLoading, refreshSettings } = useIntegration();
  const { toast } = useToast();
  const { canManage: canManagePeriods } = usePeriodManagementPermissions();

  const hasFinancialsIntegration = integrationSettings?.enabled && 
    integrationSettings?.integrationType === 'access-financials';

  const [periodSettings, setPeriodSettings] = useState<PeriodSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isRunningPeriodEnd, setIsRunningPeriodEnd] = useState(false);

  const [editYear, setEditYear] = useState<number>(new Date().getFullYear());
  const [editPeriod, setEditPeriod] = useState<number>(new Date().getMonth() + 1);
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(false);

  const [showPeriodEndDialog, setShowPeriodEndDialog] = useState(false);
  const [runDepreciation, setRunDepreciation] = useState(true);
  const [periodEndResult, setPeriodEndResult] = useState<RunPeriodEndResponse | null>(null);

  useEffect(() => {
    fetchPeriodSettings();
  }, []);

  useEffect(() => {
    refreshSettings();
  }, [refreshSettings]);

  const fetchPeriodSettings = async () => {
    setIsLoading(true);
    try {
      const settings = await periodManagementService.getPeriodSettings();
      setPeriodSettings(settings);
      setEditYear(settings.currentYear);
      setEditPeriod(settings.currentPeriod);
      setAutoSyncEnabled(settings.autoSyncEnabled);
    } catch (error) {
      console.error('Error fetching period settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load period settings. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (isDemo) {
      toast({
        title: 'Demo Mode',
        description: 'Cannot save settings in demo mode. Please switch to API mode.',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    try {
      const updatedSettings = await periodManagementService.updatePeriodSettings({
        currentYear: editYear,
        currentPeriod: editPeriod,
        autoSyncEnabled,
      });
      setPeriodSettings(updatedSettings);
      toast({
        title: 'Success',
        description: 'Period settings saved successfully.',
      });
    } catch (error) {
      console.error('Error saving period settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save period settings. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSyncNow = async () => {
    if (isDemo) {
      toast({
        title: 'Demo Mode',
        description: 'Cannot sync in demo mode. Please switch to API mode.',
        variant: 'destructive',
      });
      return;
    }

    setIsSyncing(true);
    try {
      const response: SyncPeriodResponse = await periodManagementService.syncPeriodFromFinancials({});
      if (response.success) {
        await fetchPeriodSettings();
        toast({
          title: 'Success',
          description: `Period synced to ${response.syncedYear}/${String(response.syncedPeriod).padStart(2, '0')} from ${response.syncSource}.`,
        });
      } else {
        toast({
          title: 'Sync Failed',
          description: response.errorMessage ?? 'Failed to sync period from Financials.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error syncing period:', error);
      toast({
        title: 'Error',
        description: 'Failed to sync period. Please check your Financials integration settings.',
        variant: 'destructive',
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleRunPeriodEnd = async () => {
    if (isDemo) {
      toast({
        title: 'Demo Mode',
        description: 'Cannot run period end in demo mode. Please switch to API mode.',
        variant: 'destructive',
      });
      return;
    }

    setIsRunningPeriodEnd(true);
    try {
      const response = await periodManagementService.runPeriodEnd({
        runDepreciation,
      });
      setPeriodEndResult(response);
      if (response.success) {
        await fetchPeriodSettings();
        toast({
          title: 'Period End Complete',
          description: `Advanced from ${response.previousYear}/${String(response.previousPeriod).padStart(2, '0')} to ${response.newYear}/${String(response.newPeriod).padStart(2, '0')}.`,
        });
      } else {
        toast({
          title: 'Period End Failed',
          description: response.errorMessage ?? 'Failed to run period end.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error running period end:', error);
      toast({
        title: 'Error',
        description: 'Failed to run period end. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsRunningPeriodEnd(false);
      setShowPeriodEndDialog(false);
    }
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const getLastSyncedDisplay = (): string => {
    if (!periodSettings?.lastSyncedAt) {
      return 'Never synced';
    }
    
    const dateTime = formatDateTime(periodSettings.lastSyncedAt);
    
    if (periodSettings.lastSyncSource) {
      return dateTime + ' from ' + periodSettings.lastSyncSource;
    }
    
    return dateTime;
  };

  if (isLoading || isIntegrationLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <div className="border-b bg-card p-6 flex-shrink-0">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Calendar className="h-6 w-6 text-primary" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Period Management</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Manage the active accounting period and control transaction processing
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Current Active Period
              </CardTitle>
              <CardDescription>
                The active period is used for all transactions and asset operations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="text-4xl font-bold text-primary">
                    {periodSettings?.displayPeriod ?? '-'}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    Status: <Badge variant={periodSettings?.periodStatus === 'Open' ? 'default' : 'secondary'}>
                      {periodSettings?.periodStatus ?? 'Unknown'}
                    </Badge>
                  </div>
                </div>
                {canManagePeriods && (
                  <Button
                    variant="default"
                    size="lg"
                    onClick={() => setShowPeriodEndDialog(true)}
                    disabled={isDemo}
                  >
                    <Play className="mr-2 h-4 w-4" />
                    Run Period End
                  </Button>
                )}
              </div>

              {periodSettings?.lastPeriodEndAt && (
                <div className="mt-4 pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Last Period End: {formatDateTime(periodSettings.lastPeriodEndAt)}
                    {periodSettings.lastPeriodEndBy && ` by ${periodSettings.lastPeriodEndBy}`}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className={hasFinancialsIntegration ? 'opacity-60' : ''}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings2 className="h-5 w-5" />
                Manual Period Settings
              </CardTitle>
              <CardDescription>
                {hasFinancialsIntegration 
                  ? 'Manual period settings are disabled when integrated with Financials. Use the sync feature below instead.'
                  : 'Manually set the current period when not integrated with Financials'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {hasFinancialsIntegration && (
                <Alert>
                  <LinkIcon className="h-4 w-4" />
                  <AlertTitle>Financials Integration Active</AlertTitle>
                  <AlertDescription>
                    Period settings are managed through the Financials integration. Use the sync feature below to update the period.
                  </AlertDescription>
                </Alert>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="year">Year</Label>
                  <Input
                    id="year"
                    type="number"
                    min={2000}
                    max={2100}
                    value={editYear}
                    onChange={(e) => setEditYear(parseInt(e.target.value) || new Date().getFullYear())}
                    disabled={hasFinancialsIntegration || autoSyncEnabled}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="period">Period (1-12)</Label>
                  <Input
                    id="period"
                    type="number"
                    min={1}
                    max={12}
                    value={editPeriod}
                    onChange={(e) => setEditPeriod(Math.min(12, Math.max(1, parseInt(e.target.value) || 1)))}
                    disabled={hasFinancialsIntegration || autoSyncEnabled}
                  />
                </div>
              </div>

              {canManagePeriods && (
                <div className="flex justify-end">
                  <Button
                    onClick={handleSaveSettings}
                    disabled={isSaving || isDemo || hasFinancialsIntegration || autoSyncEnabled}
                  >
                    {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save Period Settings
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className={!hasFinancialsIntegration ? 'opacity-60' : ''}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5" />
                Financials Integration Sync
              </CardTitle>
              <CardDescription>
                {hasFinancialsIntegration
                  ? 'Synchronise the period with your configured Financials integration'
                  : 'Configure a Financials integration to enable period synchronization'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!hasFinancialsIntegration && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>No Financials Integration</AlertTitle>
                  <AlertDescription>
                    To sync periods with Financials, please configure an integration in Asset Options &gt; Integrations.
                  </AlertDescription>
                </Alert>
              )}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="auto-sync">Auto-Sync Enabled</Label>
                  <p className="text-sm text-muted-foreground">
                    Automatically sync period with Financials when integration is configured
                  </p>
                </div>
                <Switch
                  id="auto-sync"
                  checked={autoSyncEnabled}
                  onCheckedChange={(checked) => {
                    setAutoSyncEnabled(checked);
                  }}
                  disabled={isDemo || !hasFinancialsIntegration}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">Last Synced</div>
                  <div className="text-sm text-muted-foreground">
                    {getLastSyncedDisplay()}
                  </div>
                </div>
                {canManagePeriods && (
                  <Button
                    variant="outline"
                    onClick={handleSyncNow}
                    disabled={isSyncing || isDemo || !hasFinancialsIntegration}
                  >
                    {isSyncing ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="mr-2 h-4 w-4" />
                    )}
                    Sync Now
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {periodEndResult && (
            <Alert variant={periodEndResult.success ? 'default' : 'destructive'}>
              {periodEndResult.success ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <AlertCircle className="h-4 w-4" />
              )}
              <AlertTitle>
                {periodEndResult.success ? 'Period End Completed' : 'Period End Failed'}
              </AlertTitle>
              <AlertDescription>
                {periodEndResult.success ? (
                  <div className="space-y-2">
                    <p>
                      Period advanced from {periodEndResult.previousYear}/{String(periodEndResult.previousPeriod).padStart(2, '0')} to {periodEndResult.newYear}/{String(periodEndResult.newPeriod).padStart(2, '0')}
                    </p>
                    {periodEndResult.depreciationResult && (
                      <div className="mt-2 p-3 bg-muted rounded-md">
                        <p className="font-medium">Depreciation Summary:</p>
                        <ul className="text-sm mt-1 space-y-1">
                          <li>Assets Processed: {periodEndResult.depreciationResult.totalAssetsProcessed}</li>
                          <li>Successful: {periodEndResult.depreciationResult.successfulTransactions}</li>
                          <li>Failed: {periodEndResult.depreciationResult.failedTransactions}</li>
                          <li>Total Amount: {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(periodEndResult.depreciationResult.totalDepreciationAmount)}</li>
                        </ul>
                      </div>
                    )}
                  </div>
                ) : (
                  periodEndResult.errorMessage
                )}
              </AlertDescription>
            </Alert>
          )}

          {isDemo && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Demo Mode</AlertTitle>
              <AlertDescription>
                Period management features are read-only in demo mode. Switch to API mode to make changes.
              </AlertDescription>
            </Alert>
          )}
        </div>
      </div>

      <Dialog open={showPeriodEndDialog} onOpenChange={setShowPeriodEndDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Run Period End</DialogTitle>
            <DialogDescription>
              This will close the current period ({periodSettings?.displayPeriod}) and advance to the next period.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="run-depreciation">Run Automatic Depreciation</Label>
                <p className="text-sm text-muted-foreground">
                  Process depreciation for all eligible assets in the current period
                </p>
              </div>
              <Switch
                id="run-depreciation"
                checked={runDepreciation}
                onCheckedChange={setRunDepreciation}
              />
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Important</AlertTitle>
              <AlertDescription>
                {runDepreciation
                  ? 'Depreciation transactions will be automatically created for all eligible assets. This action cannot be undone.'
                  : 'No depreciation will be processed. You can run depreciation manually later.'}
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowPeriodEndDialog(false)}
              disabled={isRunningPeriodEnd}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRunPeriodEnd}
              disabled={isRunningPeriodEnd}
            >
              {isRunningPeriodEnd && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Run Period End
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
